import { Box, Link as ChakraLink } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { useMapContext } from '@resistapp/client/contexts/map-context';
import { useSampleDataContext } from '@resistapp/client/contexts/sample-data-context';
import { useUrlContext } from '@resistapp/client/contexts/url-provider';
import { useOverviewContext } from '@resistapp/client/contexts/use-overview-context/use-overview-context';
import { TestSelectors } from '@resistapp/common/testing/visual-test-selectors';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { OverviewActions } from '../overview-actions/overview-actions';
import { BackButtonOnMap } from '../selected-map-sampling-point/back-button-on-map';
import { theme } from '../shared/theme';
import { AreaClickTip } from '../tooltips/contextual-tips/area-click-tip';
import { MapLegend } from './map-legend/map-legend';
import { MapboxMap } from './mapbox-map';
import { MapMarkers } from './markers/markers';

export function OverviewMapContainer() {
  const { projectId, query } = useUrlContext();
  const {
    hasPreparedData,
    notAvailableReason,
    trendData,
    previousAdminAreasLifo,
  } = useOverviewContext();
  const { changeZoomedAdminAreaStable } = useMapContext();
  const { queryFilters } = useSampleDataContext();
  // We have the fullSize map only in wastPan projects for now, for testing
  // const isWastPanProject = !!props.projectId && !!WastPanProjectIds.includes(props.projectId);
  const [isFullsize, setIsFullsize] = useState(false);
  const [isHovering, setIsHovering] = useState(false);

  if (!trendData?.length) {
    if (!hasPreparedData) {
      return <MapContainer>Loading data from the project...</MapContainer>;
    } else {
      const message = notAvailableReason
        ? `This view is not available: ${notAvailableReason}`
        : 'This view is not available for this project';
      return (
        <MapContainer>
          <Box>{message}</Box>
          <Box>
            You can still&nbsp;
            <ChakraLink as={Link} to={`/research/${projectId.viable}${query}`} style={{ textDecoration: 'underline' }}>
              analyse the data in detail
            </ChakraLink>
            .
          </Box>
        </MapContainer>
      );
    }
  }

  const previousAdminLevel = previousAdminAreasLifo.length
    ? previousAdminAreasLifo[previousAdminAreasLifo.length - 1]
    : null;
  const handleBackButtonClick = () => {
    if (previousAdminLevel) {
      changeZoomedAdminAreaStable(previousAdminLevel, { previous: true });
      queryFilters.toggleSamplingPointStable(undefined, true);
    }
  };

  const isVisualTest = window.location.search.includes('noMap=true');

  return (
    <MapContainer isFullsize={isFullsize} className={TestSelectors.TEST_MAP_CONTAINER}>
      <OverviewActionsContainer>
        <OverviewActions />
      </OverviewActionsContainer>
      {!isVisualTest && (
        <MapboxMap isFullsize={isFullsize}>
          {<MapMarkers />}
          <MapLegend showInfoIcon enableContextualTip />
          <MapToFullsizeControl
            onClick={() => {
              setIsFullsize(!isFullsize);
            }}
            style={{
              cursor: 'pointer',
            }}
          >
            <FullsizeToggleSVG isFullsize={isFullsize} isHovering={isHovering} setIsHovering={setIsHovering} />
          </MapToFullsizeControl>
          {previousAdminLevel && <BackButtonOnMap title={previousAdminLevel.name} onClick={handleBackButtonClick} />}
        </MapboxMap>
      )}
      <AreaClickTip />
    </MapContainer>
  );
}

interface FullsizeToggleSVGProps {
  isFullsize: boolean;
  isHovering: boolean;
  setIsHovering: (hovering: boolean) => void;
}

function FullsizeToggleSVG(props: FullsizeToggleSVGProps) {
  const { isFullsize, isHovering, setIsHovering } = props;
  return (
    <>
      <svg
        width="70"
        height="25"
        viewBox="0 0 100 30"
        xmlns="http://www.w3.org/2000/svg"
        onMouseEnter={() => {
          setIsHovering(true);
        }}
        onMouseLeave={() => {
          setIsHovering(false);
        }}
        style={{ marginBottom: isFullsize ? '5px' : '0px', transform: isFullsize ? 'rotate(180deg)' : 'none' }}
      >
        <polyline
          points="5,5 35,20 65,5"
          fill="none"
          stroke={isHovering ? theme.colors.neutral700 : 'black'}
          strokeWidth="7"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ shapeRendering: 'geometricPrecision' }}
        />

        <polyline
          points="5,5 35,20 65,5"
          fill="none"
          stroke={isHovering ? theme.colors.neutral700 : theme.colors.neutral500}
          strokeWidth="5"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ shapeRendering: 'geometricPrecision' }}
        />
      </svg>
    </>
  );
}

const MapContainer = styled.div<{ isFullsize?: boolean }>`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: ${props => (props.isFullsize ? '800px' : '400px')};
`;

const MapToFullsizeControl = styled.div`
  position: absolute;
  z-index: ${theme.zIndexes.overviewMapControls};
  bottom: 0;
  left: 50%;
  transform: translate(-50%, 0);
  padding-left: 20px;
  padding: 0 5px;
  cursor: pointer;
`;

const OverviewActionsContainer = styled.div`
  position: absolute;
  z-index: ${theme.zIndexes.overviewMapControls};
  top: ${theme.spacing[4]};
  right: ${theme.spacing[4]};
`;
